
import React, { useState, useEffect } from 'react';

interface StatsCardProps {
  title: string;
  value: string | number;
  change: string;
  icon: string;
  color: 'red' | 'green' | 'blue' | 'yellow' | 'purple';
  trend: 'up' | 'down' | 'neutral';
}

function StatsCard({ title, value, change, icon, color, trend }: StatsCardProps) {
  const colorClasses = {
    red: 'from-red-900/20 to-red-800/20 border-red-500/20 text-red-400',
    green: 'from-green-900/20 to-green-800/20 border-green-500/20 text-green-400',
    blue: 'from-blue-900/20 to-blue-800/20 border-blue-500/20 text-blue-400',
    yellow: 'from-yellow-900/20 to-yellow-800/20 border-yellow-500/20 text-yellow-400',
    purple: 'from-purple-900/20 to-purple-800/20 border-purple-500/20 text-purple-400'
  };

  const iconBgClasses = {
    red: 'bg-red-600',
    green: 'bg-green-600',
    blue: 'bg-blue-600',
    yellow: 'bg-yellow-600',
    purple: 'bg-purple-600'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} backdrop-blur-sm rounded-2xl border p-6`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className={`w-12 h-12 ${iconBgClasses[color]} rounded-xl flex items-center justify-center`}>
            <i className={`${icon} text-white w-6 h-6 flex items-center justify-center`}></i>
          </div>
          <div>
            <div className={`text-2xl font-bold ${colorClasses[color].split(' ')[3]}`}>{value}</div>
            <div className="text-gray-300 text-sm">{title}</div>
          </div>
        </div>
        <div className="text-right">
          <div className={`flex items-center space-x-1 text-sm ${
            trend === 'up' ? 'text-green-400' :
            trend === 'down' ? 'text-red-400' :
            'text-gray-400'
          }`}>
            <i className={`${
              trend === 'up' ? 'ri-arrow-up-line' :
              trend === 'down' ? 'ri-arrow-down-line' :
              'ri-subtract-line'
            } w-4 h-4 flex items-center justify-center`}></i>
            <span>{change}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardStats() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Fetch real data dari Supabase
  const fetchDashboardData = async () => {
    try {
      setError('');
      console.log('Fetching dashboard data...');
      
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        // Set empty data instead of error for better UX
        console.log('Supabase configuration missing, using empty data');
        setOrders([]);
        setLastUpdate(new Date());
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/get-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        console.warn(`HTTP ${response.status}: ${response.statusText}`);
        // Set empty data instead of error for better UX
        setOrders([]);
        setLastUpdate(new Date());
        return;
      }

      const result = await response.json();
      console.log('Dashboard data result:', result);
      
      if (result.success) {
        const ordersData = result.orders || [];
        console.log(`Loaded ${ordersData.length} orders`);
        setOrders(ordersData);
        setLastUpdate(new Date());
      } else {
        console.warn('Failed to fetch orders:', result.error);
        // Set empty data instead of error for better UX
        setOrders([]);
        setLastUpdate(new Date());
      }
    } catch (error: any) {
      console.warn('Error fetching dashboard data:', error);
      // Set empty data instead of error for better UX
      setOrders([]);
      setLastUpdate(new Date());
    } finally {
      setLoading(false);
    }
  };

  // Auto refresh setiap 30 detik
  useEffect(() => {
    fetchDashboardData();
    
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Hitung statistik dengan handling untuk data kosong
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  const todayOrders = orders.filter(order => {
    try {
      return new Date(order.created_at) >= todayStart;
    } catch {
      return false;
    }
  });
  
  const activeOrders = orders.filter(order => 
    ['pending', 'confirmed', 'preparing', 'ready'].includes(order.status)
  );
  
  const completedOrders = orders.filter(order => {
    try {
      return ['served', 'completed'].includes(order.status) && new Date(order.created_at) >= todayStart;
    } catch {
      return false;
    }
  });
  
  const paidOrders = orders.filter(order => {
    try {
      return order.payment_status === 'paid' && new Date(order.created_at) >= todayStart;
    } catch {
      return false;
    }
  });
  
  const totalSalesToday = paidOrders.reduce((total, order) => 
    total + (Number(order.total_amount) || 0), 0
  );
  
  const uniqueCustomersToday = new Set(
    todayOrders.map(order => order.customer_phone || order.customer_name).filter(id => id)
  ).size;
  
  const occupiedTables = new Set(
    activeOrders.map(order => order.table_number).filter(table => table && table > 0)
  ).size;
  
  // Menu terlaris dengan handling data kosong
  const menuSales = todayOrders.reduce((acc: any, order) => {
    try {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach((item: any) => {
          if (item && item.name) {
            if (!acc[item.name]) {
              acc[item.name] = { name: item.name, sold: 0, revenue: 0 };
            }
            acc[item.name].sold += Number(item.quantity) || 0;
            acc[item.name].revenue += (Number(item.price) || 0) * (Number(item.quantity) || 0);
          }
        });
      }
    } catch (error) {
      console.warn('Error processing order items:', error);
    }
    return acc;
  }, {});
  
  const topMenus = Object.values(menuSales)
    .sort((a: any, b: any) => b.sold - a.sold)
    .slice(0, 4);

  // Aktivitas terbaru dengan handling data kosong
  const recentActivities = [...orders]
    .sort((a, b) => {
      try {
        return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
      } catch {
        return 0;
      }
    })
    .slice(0, 5)
    .map(order => {
      try {
        const isNew = new Date(order.created_at).getTime() > Date.now() - 5 * 60 * 1000;
        const isPaid = order.payment_status === 'paid';
        
        return {
          id: order.id,
          type: isNew ? 'order' : isPaid ? 'payment' : 'status',
          message: isNew 
            ? `Pesanan baru ${order.order_number} dari meja ${order.table_number || 'takeaway'}`
            : isPaid
            ? `Pembayaran berhasil - Rp ${Number(order.total_amount || 0).toLocaleString('id-ID')}`
            : `Status ${order.order_number} berubah ke ${order.status}`,
          time: formatTimeAgo(new Date(order.updated_at || order.created_at)),
          icon: isNew ? 'ri-shopping-bag-line' : 
                isPaid ? 'ri-money-dollar-circle-line' :
                'ri-refresh-line',
          color: isNew ? 'text-green-400' :
                 isPaid ? 'text-blue-400' :
                 'text-yellow-400'
        };
      } catch {
        return {
          id: order.id,
          type: 'status',
          message: 'Aktivitas tidak valid',
          time: 'Tidak diketahui',
          icon: 'ri-alert-line',
          color: 'text-gray-400'
        };
      }
    });

  function formatTimeAgo(date: Date) {
    try {
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Baru saja';
      if (diffMins < 60) return `${diffMins} menit lalu`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} jam lalu`;
      
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} hari lalu`;
    } catch {
      return 'Tidak diketahui';
    }
  }

  // Stats untuk kartu dengan empty state yang lebih baik
  const stats = [
    {
      title: 'Total Penjualan Hari Ini',
      value: totalSalesToday > 0 ? `Rp ${totalSalesToday.toLocaleString('id-ID')}` : 'Rp 0',
      change: paidOrders.length > 0 ? `${paidOrders.length} transaksi` : 'Belum ada transaksi',
      icon: 'ri-money-dollar-circle-line',
      color: 'green' as const,
      trend: totalSalesToday > 0 ? 'up' as const : 'neutral' as const
    },
    {
      title: 'Pesanan Aktif',
      value: activeOrders.length,
      change: activeOrders.length > 0 ? `${activeOrders.filter(o => o.status === 'pending').length} pending` : 'Tidak ada pesanan',
      icon: 'ri-restaurant-line',
      color: 'red' as const,
      trend: activeOrders.length > 0 ? 'up' as const : 'neutral' as const
    },
    {
      title: 'Pelanggan Hari Ini',
      value: uniqueCustomersToday || todayOrders.length,
      change: todayOrders.length > 0 ? `${todayOrders.length} pesanan` : 'Belum ada pelanggan',
      icon: 'ri-user-line',
      color: 'blue' as const,
      trend: todayOrders.length > 0 ? 'up' as const : 'neutral' as const
    },
    {
      title: 'Meja Terisi',
      value: `${occupiedTables}/20`,
      change: `${20 - occupiedTables} tersedia`,
      icon: 'ri-table-line',
      color: 'yellow' as const,
      trend: 'neutral' as const
    },
    {
      title: 'Menu Terlaris',
      value: topMenus.length > 0 ? (topMenus[0] as any).sold : 0,
      change: topMenus.length > 0 ? (topMenus[0] as any).name.split(' ').slice(0, 2).join(' ') : 'Belum ada data',
      icon: 'ri-star-line',
      color: 'purple' as const,
      trend: topMenus.length > 0 ? 'up' as const : 'neutral' as const
    },
    {
      title: 'Pesanan Selesai',
      value: completedOrders.length,
      change: todayOrders.length > 0 ? `${Math.round(completedOrders.length / todayOrders.length * 100)}% selesai` : '0% selesai',
      icon: 'ri-check-line',
      color: 'green' as const,
      trend: completedOrders.length > 0 ? 'up' as const : 'neutral' as const
    }
  ];

  // Data penjualan 7 hari dengan empty state yang lebih baik
  const salesData = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    
    const dayOrders = orders.filter(order => {
      try {
        const orderDate = new Date(order.created_at);
        return orderDate.toDateString() === date.toDateString() && order.payment_status === 'paid';
      } catch {
        return false;
      }
    });
    
    const sales = dayOrders.reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0);
    
    return {
      day: ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'][date.getDay()],
      sales: sales / 1000000, // Convert ke jutaan
      raw: sales
    };
  });

  const maxSales = Math.max(...salesData.map(d => d.sales), 0.1); // Minimum 0.1 untuk display

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-red-500 text-4xl mb-4 w-12 h-12 flex items-center justify-center mx-auto animate-spin"></i>
          <p className="text-gray-300">Memuat data dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Update Indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-sm text-gray-400">
          <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
          <span>Terakhir update: {lastUpdate.toLocaleTimeString('id-ID')}</span>
          <span className="text-gray-500">•</span>
          <span>{orders.length} total pesanan</span>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={fetchDashboardData}
            className="flex items-center space-x-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-refresh-line w-3 h-3 flex items-center justify-center"></i>
            <span>Refresh</span>
          </button>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-green-400 text-sm">
              {orders.length > 0 ? 'Live Data' : 'Ready'}
            </span>
          </div>
        </div>
      </div>

      {/* Status Banner untuk Empty State */}
      {orders.length === 0 && (
        <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl border border-blue-500/20 p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <i className="ri-information-line text-white w-6 h-6 flex items-center justify-center"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Dashboard Siap Digunakan</h3>
              <p className="text-gray-300">Sistem dashboard berjalan normal. Data akan muncul setelah ada pesanan masuk.</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Sales Chart */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-red-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6">Penjualan 7 Hari Terakhir</h3>
          
          <div className="space-y-4">
            {salesData.map((item, index) => (
              <div key={index} className="flex items-center space-x-4">
                <div className="w-8 text-gray-400 text-sm">{item.day}</div>
                <div className="flex-1 bg-gray-700 rounded-full h-4 relative">
                  <div 
                    className={`bg-red-500 h-4 rounded-full transition-all duration-1000 ${
                      item.sales > 0 ? 'opacity-100' : 'opacity-20'
                    }`}
                    style={{ width: `${Math.max((item.sales / maxSales) * 100, 3)}%` }}
                  ></div>
                </div>
                <div className="w-24 text-white text-sm font-semibold text-right">
                  {item.sales > 0 ? `${item.sales.toFixed(1)}M` : '0'}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 text-center">
            <div className="text-2xl font-bold text-white">
              Rp {salesData.reduce((sum, d) => sum + d.raw, 0).toLocaleString('id-ID')}
            </div>
            <div className="text-gray-400 text-sm">
              {salesData.reduce((sum, d) => sum + d.raw, 0) > 0 ? 'Total 7 Hari Terakhir' : 'Belum ada penjualan'}
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-red-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6">Aktivitas Terbaru</h3>
          
          <div className="space-y-4 max-h-80 overflow-y-auto">
            {recentActivities.length > 0 ? recentActivities.map((activity, index) => (
              <div key={`${activity.id}-${index}`} className="flex items-start space-x-3 p-3 bg-gray-900/30 rounded-xl">
                <div className={`w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0`}>
                  <i className={`${activity.icon} ${activity.color} w-4 h-4 flex items-center justify-center`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm">{activity.message}</p>
                  <p className="text-gray-400 text-xs mt-1">{activity.time}</p>
                </div>
              </div>
            )) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-gray-400 text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h4 className="text-white font-semibold mb-2">Belum Ada Aktivitas</h4>
                <p className="text-gray-400 text-sm">Aktivitas akan muncul setelah ada pesanan masuk</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Selling Items */}
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-red-500/20 p-6">
        <h3 className="text-xl font-bold text-white mb-6">Menu Terlaris Hari Ini</h3>
        
        {topMenus.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {topMenus.map((item: any, index) => (
              <div key={index} className="bg-gray-900/30 rounded-2xl p-4">
                <div className="text-center">
                  <div className="text-4xl mb-2">
                    {item.name.toLowerCase().includes('ramen') ? '🍜' :
                     item.name.toLowerCase().includes('sushi') ? '🍣' :
                     item.name.toLowerCase().includes('tempura') ? '🍤' :
                     item.name.toLowerCase().includes('gyoza') ? '🥟' :
                     item.name.toLowerCase().includes('matcha') ? '🍵' :
                     item.name.toLowerCase().includes('ramune') ? '🥤' :
                     item.name.toLowerCase().includes('tea') ? '🍃' :
                     '🍽️'}
                  </div>
                  <h4 className="text-white font-semibold mb-2 text-sm line-clamp-2">{item.name}</h4>
                  <div className="space-y-1">
                    <div className="text-red-400 font-bold">{item.sold} terjual</div>
                    <div className="text-gray-400 text-xs">
                      Rp {item.revenue.toLocaleString('id-ID')}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-restaurant-line text-gray-400 text-3xl w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h4 className="text-white font-semibold mb-2">Belum Ada Penjualan Menu</h4>
            <p className="text-gray-400 mb-4">Data menu terlaris akan muncul setelah ada transaksi hari ini</p>
            <div className="text-sm text-gray-500">
              Dashboard siap melacak penjualan menu secara real-time
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
